import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Unauthorized from '../../auth/Unauthorized';
import { Link, useLocation } from "react-router-dom";
import { styled } from "styled-components";
import {
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Card,
    CardContent,
    Menu,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Box,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    LinearProgress
} from "@mui/material";
import { Add, Edit, Delete, MoreVert, Logout, Info } from "@mui/icons-material";
import { Avatar } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import CadData from "../cadData/CadData";
import CadDataDX from "../cadDataDX/CadDataDX";
import CadDataDXUpload from "../cadDataDX/CadDataDXUpload";
import StyleColorImg from "../styleColorImg/StyleColorImg";
import { getSocket } from "../../../services/socketManager";
import { useUser } from '../../../contexts/UserContext';
import { logAccess } from '../../../utils/userLog';
import { AccessManagement } from "./AccessManagement";


// Socket.IO 싱글톤 인스턴스 가져오기
const socket = getSocket();

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background: white;
`;

const NavBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-bottom: 1.5px solid rgba(204, 197, 185, 0.71);
`;

const NavItems = styled.div`
  display: flex;
  gap: 8px;
`;

const NavItem = styled.div`
  padding: 6px 12px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  background: ${props => props.$active ? '#ccc5b9 !important' : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled(Avatar)`
  width: 32px !important;
  height: 32px !important;
  background: #e0e0e0 !important;
  color: #666 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.span`
  font-size: 14px;
  color: #2c3e50;
  line-height: 1.2;
`;

const UserDepartment = styled.span`
  font-size: 11px;
  color: #2c3e50;
  line-height: 1.2;
`;

const LogoutButton = styled.div`
  padding: 6px 16px !important;
  border-radius: 6px !important;
  color: #666 !important;
  text-transform: none !important;
  transition: all 0.2s ease !important;
  cursor: pointer;
  
  &:hover {
    background: #f5f5f5 !important;
    color: #333 !important;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow: auto;
`;

const CadDataContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 0 16px 0;
  width: 100%;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  min-width: fit-content;
`;

const StyledCard = styled(Card)`
  min-width: 280px;
  width: 280px;
  height: 200px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid #ccc5b9;
  cursor: pointer;
  overflow: hidden;
  background: #fafafa;
  box-shadow: none;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #b8b0a5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CardContentStyled = styled(CardContent)`
  flex: 1;
  padding: 0px !important;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  overflow: hidden;
  min-height: 0;
`;

const CardFooter = styled.div`
  padding: 12px 20px;
  background: #f5f5f5;
  border-top: 1px solid #ccc5b9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 10;
  flex-shrink: 0;
`;

const MenuLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

// 썸네일 미리보기 컨테이너
const ThumbnailPreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

// 실제 썸네일 이미지
const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// 스켈레톤 로딩 컨테이너 (깜빡임 효과)
const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #e8e8e8;
  border-radius: 4px;
  animation: blink 1.2s ease-in-out infinite;
  
  @keyframes blink {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`;

const MenuTitle = styled(Typography)`
  font-size: 14px !important;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const CardMeta = styled(Typography)`
  font-size: 0.7rem !important;
  color: #7f8c8d;
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const AddCard = styled.div`
  min-width: 280px;
  width: 280px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 2px dashed rgba(0, 0, 0, 0.12);
  background: #fafafa;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #ccc5b9;
    background: #f0f0f0;
  }
`;

const AddIcon = styled(Add)`
  font-size: 48px !important;
  color: #999 !important;
`;

const CardMenuButton = styled(IconButton)`
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
  color: #999 !important;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05) !important;
  }
`;

const formatTimeAgo = (dateString) => {
    if (!dateString) return '방금 전';

    // 크롬 브라우저 호환성을 위한 강화된 날짜 파싱
    let date;
    try {
        if (typeof dateString === 'string') {
            // ISO 8601 형식의 문자열인 경우
            if (dateString.includes('T')) {
                // UTC 표시가 없으면 추가 (크롬에서 시간대 문제 방지)
                let normalizedString = dateString.trim();

                // 시간대 정보가 없으면 UTC로 간주
                if (!normalizedString.endsWith('Z') && !normalizedString.includes('+') && !normalizedString.includes('-', 10)) {
                    normalizedString = normalizedString + 'Z';
                }

                // 크롬에서 안정적으로 파싱되도록 명시적 UTC 파싱
                date = new Date(normalizedString);
            } else {
                // 일반 날짜 문자열 (YYYY-MM-DD 형식 등)
                // 크롬에서는 로컬 시간대로 해석될 수 있으므로 UTC로 명시적 변환
                const dateOnly = dateString.trim();
                if (dateOnly.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    // YYYY-MM-DD 형식인 경우 UTC 자정으로 파싱
                    date = new Date(dateOnly + 'T00:00:00.000Z');
                } else {
                    date = new Date(dateString);
                }
            }
        } else if (dateString instanceof Date) {
            date = dateString;
        } else {
            // 숫자 타임스탬프나 기타 형식
            date = new Date(dateString);
        }

        // 유효하지 않은 날짜인 경우
        if (isNaN(date.getTime())) {
            console.warn('⚠️ 잘못된 날짜 형식:', dateString, '→ 방금 전으로 표시');
            return '방금 전';
        }
    } catch (error) {
        console.error('⚠️ 날짜 파싱 오류:', dateString, error);
        return '방금 전';
    }

    // 현재 시간을 UTC 기준으로 계산 (크롬 호환성)
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // 음수 차이는 미래 시간이므로 "방금 전"으로 처리
    if (diffMs < 0) {
        return '방금 전';
    }

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    let result;
    // 1분 미만은 "방금 전"으로 표시
    if (diffMins < 1) result = '방금 전';
    // 1분 이상부터 시간 표시
    else if (diffMins < 60) result = `${diffMins}분 전`;
    else if (diffHours < 24) result = `${diffHours}시간 전`;
    else if (diffDays < 7) result = `${diffDays}일 전`;
    else result = date.toLocaleDateString('ko-KR');

    return result;
};

const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const result = date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
    return result;
};

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 16px;
    background: white;
  }
`;

const DialogTitleStyled = styled(DialogTitle)`
  background: #ccc5b9;
  font-weight: 600;
`;

const ThumbnailUploadArea = styled.div`

  padding: 20px;
  border: 2px dashed ${props => props.isDragging ? '#ccc5b9' : '#d1d5db'};
  text-align: center;
  background: ${props => props.isDragging ? 'rgba(204, 197, 185, 0.1)' : '#fafafa'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #ccc5b9;
    background: rgba(204, 197, 185, 0.05);
  }
`;

const ThumbnailPreviewArea = styled.div`
  margin-top: 16px;
  position: relative;
  display: flex;
  justify-content: center;
`;

const ThumbnailPreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const RemoveThumbnailButton = styled(IconButton)`
  position: absolute !important;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.9) !important;
  
  &:hover {
    background: rgba(255, 255, 255, 1) !important;
  }
`;

// 썸네일 이미지 컴포넌트 (타임아웃 처리 포함)
function ThumbnailImageWithTimeout({ item, imageLoadingState, onLoadSuccess, onLoadError, onLoadStart }) {
    useEffect(() => {
        // 이미지 로딩이 시작되었고 10초 후에도 완료되지 않으면 타임아웃 처리
        if (imageLoadingState === true) {
            const timeout = setTimeout(() => {
                console.warn('⏱️ 썸네일 로드 타임아웃:', item.id, item.thumbnailUrl);
                onLoadError();
            }, 10000); // 10초 타임아웃

            return () => clearTimeout(timeout);
        }
    }, [imageLoadingState, item.id, item.thumbnailUrl, onLoadError]);

    // 썸네일 업데이트 타임스탬프가 있으면 사용, 없으면 updatedAt을 사용하여 캐시 무효화
    // updatedAt이 있으면 이를 밀리초로 변환하여 사용 (서버에서 업데이트된 시간)
    // 맥 크롬의 강력한 캐시를 우회하기 위해 추가 파라미터 사용
    let timestamp;
    if (item.thumbnailUpdatedAt) {
        timestamp = item.thumbnailUpdatedAt;
    } else if (item.updatedAt) {
        // updatedAt을 밀리초로 변환 (서버에서 받은 시간)
        timestamp = new Date(item.updatedAt).getTime();
    } else {
        // 둘 다 없으면 현재 시간 사용
        timestamp = Date.now();
    }

    // 맥 크롬 캐시 우회를 위해 여러 파라미터 추가 (Date.now()는 제거하여 무한 루프 방지)
    const cacheBuster = `${timestamp}_${item.id}`;
    const imageUrl = `${item.thumbnailUrl}${item.thumbnailUrl.includes('?') ? '&' : '?'}t=${timestamp}&v=${cacheBuster}`;

    return (
        <ThumbnailImage
            key={`${item.id}-${item.thumbnailUpdatedAt || timestamp}-${cacheBuster}`} // 맥 크롬 캐시 우회를 위해 더 강력한 key 사용
            src={imageUrl}
            alt={item.title}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: imageLoadingState === false ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                pointerEvents: 'none'
            }}
            onLoad={() => {
                // console.log('✅ 썸네일 로드 성공:', item.id, item.thumbnailUrl);
                onLoadSuccess();
            }}
            onError={(e) => {
                console.error('❌ 썸네일 로드 실패:', item.thumbnailUrl, item.id);
                onLoadError();
            }}
            onLoadStart={() => {
                onLoadStart();
            }}
        />
    );
}

function Files() {
    const { instance, accounts } = useMsal();
    const { user, permissions, brands, hasPermission, loading: userLoading } = useUser();
    const [activeBrdCd, setActiveBrdCd] = useState(null);
    const [brandMenuAnchor, setBrandMenuAnchor] = useState(null);
    const [xSubTab, setXSubTab] = useState('caddata'); // 'caddata' | 'upload'

    // brands 로드 완료 시 activeBrdCd 초기화 (최초 1회)
    useEffect(() => {
        if (brands && brands.length > 0 && activeBrdCd === null) {
            const saved = localStorage.getItem('matrixBoardActiveBrdCd');
            const validSaved = saved && brands.some(b => b.brd_cd === saved) ? saved : null;
            setActiveBrdCd(validSaved || brands[0]?.brd_cd || 'M');
        }
    }, [brands]);

    // 페이지 진입 로그 (user + activeBrdCd 준비된 시점에 1회)
    const pageLoggedRef = useRef(false);
    useEffect(() => {
        if (user && activeBrdCd && !pageLoggedRef.current) {
            pageLoggedRef.current = true;
            logAccess({
                user: `${user.userId}(${user.name})`,
                email: user.email,
                brd_cd: user.brdCd || null,
                page_brd_cd: activeBrdCd,
                page_nm: 'matrix-board',
            });
        }
    }, [user, activeBrdCd]);

    const location = useLocation();
    const [menuItems, setMenuItems] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ title: '', mapType: '', season: '', adultKids: '', category: '', itemDomain: '', columnCount: '', autoTemplate: true, autoUpdate: true, uploadThumbnail: false, thumbnailFile: null, thumbnailUrl: null });
    const seasonValue = formData.season === 'Non-Season' ? 'N' : formData.season;
    const [loading, setLoading] = useState(true);
    const [isDraggingThumbnail, setIsDraggingThumbnail] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedItemForMenu, setSelectedItemForMenu] = useState(null);
    const [openInfoDialog, setOpenInfoDialog] = useState(false);
    const [selectedItemForInfo, setSelectedItemForInfo] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [templateProgress, setTemplateProgress] = useState({ message: '', progress: 0, currentSheet: 0, totalSheets: 0 });
    // MatrixBoard에서 돌아올 때 이전 탭 복원
    const [activeNav, setActiveNav] = useState(() => {
        // localStorage에서 activeNav 복원
        const savedActiveNav = localStorage.getItem('matrixBoardActiveNav');
        return location.state?.activeTab || savedActiveNav || '공유 프로젝트';
    });
    const [userPhoto, setUserPhoto] = useState(null);
    const [sortOrder, setSortOrder] = useState('latest-updated'); // 정렬 순서: 'latest-created', 'latest-updated', 'title-asc'
    const [imageLoadingStates, setImageLoadingStates] = useState({}); // 각 카드의 이미지 로딩 상태

    // 로그아웃 핸들러
    const handleLogoutInternal = () => {
        instance.logoutPopup({
            postLogoutRedirectUri: "/",
        }).catch(e => {
            console.error("로그아웃 실패:", e);
        });
    };

    const [seasonOptions, setSeasonOptions] = useState([]);

    // 아이템/도메인 옵션
    const itemDomainOptions = ['아이템', '라이프스타일', '도메인'];

    // 템플릿 타입 옵션
    const mapTypeOptions = ['판매맵', '캐드맵'];

    // 성인/키즈 옵션
    const adultKidsOptions = ['모두', '성인', '키즈'];

    // 대분류 옵션
    const categoryOptions = ['모두', '의류', '용품', '신발'];

    // 열개수 옵션
    const columnCountOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

    // 권한 체크: 권한이 없는 탭에 있으면 공유 프로젝트로 이동
    useEffect(() => {
        if (userLoading) return;
        if (activeNav === '권한 관리' && !hasPermission('accessManagement', 'view', activeBrdCd)) {
            console.log('⚠️ 권한 관리 페이지 접근 권한 없음 → 공유 프로젝트로 이동');
            setActiveNav('공유 프로젝트');
            localStorage.setItem('matrixBoardActiveNav', '공유 프로젝트');
        } else if (activeNav === '개인 프로젝트' && !hasPermission('matrixBoard-personalProject', 'view', activeBrdCd)) {
            console.log('⚠️ 개인 프로젝트 접근 권한 없음 → 공유 프로젝트로 이동');
            setActiveNav('공유 프로젝트');
            localStorage.setItem('matrixBoardActiveNav', '공유 프로젝트');
        }
    }, [userLoading, activeNav, hasPermission, activeBrdCd]);

    // activeNav 변경 시 즉시 menuItems 초기화 (깜빡임 방지)
    useEffect(() => {
        setMenuItems([]);
        setImageLoadingStates({}); // 이미지 로딩 상태도 초기화
        setLoading(true);
    }, [activeNav]);

    // loadCards를 ref로 저장하여 useEffect에서 사용
    const loadCardsRef = useRef(null);

    // 서버에서 카드 목록 불러오기
    useEffect(() => {
        if (activeBrdCd) loadCards();
    }, [activeNav, activeBrdCd]); // activeNav 또는 브랜드 변경 시 다시 로드

    // WebSocket 이벤트 리스너 설정
    useEffect(() => {
        const handleFilesUpdate = (data) => {
            console.log('📥 파일 목록 업데이트 수신:', data);
            if (data.type === 'sync' && data.files) {
                // 서버에서 최신 목록 다시 불러오기
                if (loadCardsRef.current) {
                    loadCardsRef.current();
                }
            } else if (data.type === 'add' && data.file) {
                // 새 파일이 현재 탭과 일치하는 경우만 추가
                const currentProjectType = activeNav === '공유 프로젝트' ? 'shared' :
                    activeNav === '개인 프로젝트' ? 'personal' : null;
                const fileProjectType = data.file.project_type || data.file.projectType;

                // 프로젝트 타입이 일치하거나 현재 탭이 없는 경우 추가
                if (!currentProjectType || fileProjectType === currentProjectType) {
                    // thumbnail_url을 thumbnailUrl로 변환 (서버에서 thumbnail_url로 올 수 있음)
                    const fileData = {
                        ...data.file,
                        thumbnailUrl: data.file.thumbnailUrl || data.file.thumbnail_url
                    };

                    setMenuItems(prev => {
                        // 중복 체크 (이미 있는 파일은 추가하지 않음)
                        const exists = prev.find(item => item.id === fileData.id);
                        if (exists) {
                            console.log('⚠️ 이미 존재하는 파일, 업데이트만 수행:', fileData.id);
                            return prev.map(item =>
                                item.id === fileData.id ? { ...fileData, ...item } : item
                            );
                        }
                        // console.log('✅ 새 파일 목록에 추가:', fileData.id, { thumbnailUrl: fileData.thumbnailUrl });
                        return [...prev, fileData];
                    });
                    // 새 카드의 이미지 로딩 상태 설정
                    if (fileData.thumbnailUrl) {
                        setImageLoadingStates(prev => ({
                            ...prev,
                            [fileData.id]: true
                        }));
                    }
                } else {
                    console.log('⏭️ 프로젝트 타입 불일치로 추가 건너뜀:', {
                        fileProjectType,
                        currentProjectType
                    });
                }
            } else if (data.type === 'delete' && data.fileId) {
                // 파일 삭제
                console.log('🗑️ 파일 삭제:', data.fileId);
                setMenuItems(prev => prev.filter(item => item.id !== data.fileId));
            } else if (data.type === 'update' && data.file) {
                // 파일 업데이트
                console.log('🔄 파일 업데이트:', data.file.id);
                setMenuItems(prev =>
                    prev.map(item =>
                        item.id === data.file.id ? { ...item, ...data.file } : item
                    )
                );
                // 업데이트된 파일의 이미지 로딩 상태 설정
                if (data.file.thumbnailUrl) {
                    setImageLoadingStates(prev => ({
                        ...prev,
                        [data.file.id]: true
                    }));
                }
            }
        };

        const handleThumbnailUpdate = (data) => {
            console.log('📸 썸네일 업데이트 수신:', data);
            if (data.fileId && data.thumbnailUrl) {
                // 해당 파일의 썸네일만 업데이트 (URL 또는 updatedAt이 실제로 변경된 경우에만)
                setMenuItems(prev => {
                    const targetItem = prev.find(item => item.id === data.fileId);

                    // 썸네일 URL이 실제로 변경되었는지 확인
                    const urlChanged = targetItem && targetItem.thumbnailUrl !== data.thumbnailUrl;
                    console.log('  - URL 변경 여부:', urlChanged);

                    // updatedAt이 변경되었는지 확인 (타임스탬프 비교)
                    const updatedAtChanged = targetItem && data.updatedAt &&
                        targetItem.updatedAt !== data.updatedAt &&
                        new Date(data.updatedAt).getTime() > new Date(targetItem.updatedAt || 0).getTime();

                    // URL과 updatedAt이 모두 동일하면 재로딩하지 않음
                    if (!urlChanged && !updatedAtChanged && targetItem && targetItem.thumbnailUrl) {
                        console.log('ℹ️ 썸네일 URL과 updatedAt이 동일하여 재로딩하지 않음:', data.fileId);
                        return prev;
                    }

                    // URL 또는 updatedAt이 변경되었거나 썸네일이 없었던 경우에만 업데이트 및 로딩 상태 재설정
                    if (urlChanged || updatedAtChanged || !targetItem || !targetItem.thumbnailUrl) {
                        setImageLoadingStates(prevStates => ({
                            ...prevStates,
                            [data.fileId]: true
                        }));
                    }

                    // URL 또는 updatedAt이 변경되었거나 썸네일이 없었던 경우에만 업데이트
                    return prev.map(item => {
                        if (item.id === data.fileId) {
                            return {
                                ...item,
                                thumbnailUrl: data.thumbnailUrl,
                                updatedAt: data.updatedAt || new Date().toISOString(),
                                thumbnailUpdatedAt: Date.now() // 이미지 강제 재로딩을 위한 타임스탬프
                            };
                        }
                        return item;
                    });
                });
            }
        };

        socket.on('files-update', handleFilesUpdate);
        socket.on('thumbnail-update', handleThumbnailUpdate);

        return () => {
            socket.off('files-update', handleFilesUpdate);
            socket.off('thumbnail-update', handleThumbnailUpdate);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeNav]);

    // 다이얼로그 열릴 때 기획시즌 옵션 조회
    const DEFAULT_SEASONS = ['Non-Season', '27F', '27S', '26F', '26N', '26S', '25F', '25N', '25S', '24F', '24N', '24S'];
    useEffect(() => {
        if (openDialog && !editingItem) {
            if (formData.mapType === '캐드맵') {
                const brdCd = activeBrdCd || 'M';
                fetch(`/api/matrix-board/season-options?brdCd=${encodeURIComponent(brdCd)}`)
                    .then(r => r.json())
                    .then(result => { if (result.success) setSeasonOptions(result.data); })
                    .catch(() => setSeasonOptions(DEFAULT_SEASONS));
            } else {
                setSeasonOptions(DEFAULT_SEASONS);
            }
        }
    }, [openDialog, editingItem, activeBrdCd, formData.mapType]);


    // 다이얼로그에서 붙여넣기 이벤트 등록
    useEffect(() => {
        if (openDialog && formData.uploadThumbnail) {
            document.addEventListener('paste', handleThumbnailPaste);
            return () => {
                document.removeEventListener('paste', handleThumbnailPaste);
            };
        }
    }, [openDialog, formData.uploadThumbnail]);

    // 사용자 프로필 사진 가져오기
    useEffect(() => {
        let photoUrl = null;

        const fetchUserPhoto = async () => {
            if (accounts && accounts.length > 0) {
                try {
                    // Graph API 호출을 위한 토큰 획득
                    const response = await instance.acquireTokenSilent({
                        scopes: ["User.Read"],
                        account: accounts[0]
                    });

                    // Graph API로 프로필 사진 요청
                    const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
                        headers: {
                            'Authorization': `Bearer ${response.accessToken}`
                        }
                    });

                    if (photoResponse.ok) {
                        const blob = await photoResponse.blob();
                        photoUrl = URL.createObjectURL(blob);
                        setUserPhoto(photoUrl);
                    }
                } catch (error) {
                    console.log('프로필 사진을 가져올 수 없습니다:', error);
                    // 사진이 없거나 에러가 발생하면 기본 아바타 사용
                }
            }
        };

        fetchUserPhoto();

        // cleanup: Blob URL 해제
        return () => {
            if (photoUrl) {
                URL.revokeObjectURL(photoUrl);
            }
        };
    }, [instance, accounts]);

    const loadCards = useCallback(async () => {
        try {
            setLoading(true);
            // activeNav에 따라 projectType 결정
            let projectType = '';
            if (activeNav === '공유 프로젝트') {
                projectType = 'shared';
            } else if (activeNav === '개인 프로젝트') {
                projectType = 'personal';
            }

            // projectType이 있으면 query parameter로 전달
            // 개인 프로젝트일 때는 사용자 정보도 함께 전달
            let url = projectType ? `/api/matrix-board/list?projectType=${projectType}` : '/api/matrix-board/list';
            if (projectType === 'personal') {
                const userName = getUserName();
                if (userName) {
                    url += `&createdBy=${encodeURIComponent(userName)}`;
                }
            }
            if (activeBrdCd) {
                url += `&brdCd=${encodeURIComponent(activeBrdCd)}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success) {
                result.data.slice(0, 3).forEach((item, idx) => {
                });

                // 서버에서 가져온 데이터를 그대로 사용 (localStorage 병합 제거)
                setMenuItems(result.data);
                // 이미지 로딩 상태 업데이트 (이미 로드된 썸네일은 유지, 새로운 썸네일만 로딩 중으로 설정)
                setImageLoadingStates(prev => {
                    const newStates = { ...prev };
                    result.data.forEach(item => {
                        if (item.thumbnailUrl) {
                            // 이미 로드된 썸네일은 상태 유지, 새로운 썸네일만 로딩 중으로 설정
                            if (newStates[item.id] === undefined) {
                                newStates[item.id] = true;
                            }
                        }
                    });
                    return newStates;
                });
            } else {
                console.error('카드 목록 로드 실패:', result.message);
                setMenuItems([]); // 실패 시 빈 배열로 설정
            }
        } catch (error) {
            console.error('카드 목록 로드 오류:', error);
            setMenuItems([]); // 오류 시 빈 배열로 설정
        } finally {
            setLoading(false);
        }
    }, [activeNav, activeBrdCd, accounts]);

    // loadCards를 ref에 저장
    useEffect(() => {
        loadCardsRef.current = loadCards;
    }, [loadCards]);


    const handleOpenAdd = () => {
        setEditingItem(null);
        const isMBrand = !activeBrdCd || ['CB', '?', '-'].includes(activeBrdCd.toUpperCase()) || activeBrdCd.toUpperCase() === 'M';
        setFormData({ title: '', mapType: '', season: '', adultKids: isMBrand ? '성인' : '', category: '', itemDomain: '', columnCount: '', autoTemplate: true, autoUpdate: true, uploadThumbnail: false, thumbnailFile: null, thumbnailUrl: null });
        setOpenDialog(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);

        // 썸네일이 직접 업로드된 경우에만 체크박스 체크 및 이미지 URL 저장
        // thumbnail_uploaded가 false면 자동 생성된 썸네일이므로 미리보기 표시 안 함
        const uploadThumbnail = item.thumbnailUploaded && item.thumbnailUrl ? true : false;
        const thumbnailUrl = item.thumbnailUploaded && item.thumbnailUrl ? item.thumbnailUrl : null;

        setFormData({
            title: item.title || '',
            mapType: item.mapType || '',
            season: item.season || '',
            adultKids: item.adultKids || '',
            category: item.category || '',
            itemDomain: item.itemDomain || '',
            columnCount: item.columnCount || '',
            autoTemplate: item.autoTemplate || false,
            autoUpdate: item.autoUpdate || false,
            uploadThumbnail: uploadThumbnail,
            thumbnailFile: null,
            thumbnailUrl: thumbnailUrl
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ title: '', mapType: '', season: '', adultKids: '', category: '', itemDomain: '', columnCount: '', autoTemplate: false, autoUpdate: false, uploadThumbnail: false, thumbnailFile: null, thumbnailUrl: null });
    };

    // 썸네일 이미지 파일 선택 핸들러
    const handleThumbnailFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            setFormData({ ...formData, thumbnailFile: file });
        } else {
            alert('이미지 파일만 업로드할 수 있습니다.');
        }
    };

    // 썸네일 드래그 앤 드롭 핸들러
    const handleThumbnailDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingThumbnail(true);
    };

    const handleThumbnailDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingThumbnail(false);
    };

    const handleThumbnailDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingThumbnail(false);

        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
            setFormData({ ...formData, thumbnailFile: file });
        } else {
            alert('이미지 파일만 업로드할 수 있습니다.');
        }
    };

    // 썸네일 붙여넣기 핸들러
    const handleThumbnailPaste = useCallback((e) => {
        const items = e.clipboardData?.items;
        if (items) {
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const file = items[i].getAsFile();
                    if (file) {
                        setFormData(prev => ({ ...prev, thumbnailFile: file }));
                        break;
                    }
                }
            }
        }
    }, []);

    // 썸네일 제거 핸들러
    const handleRemoveThumbnail = () => {
        setFormData({ ...formData, thumbnailFile: null, thumbnailUrl: null });
    };

    const handleSave = async (e) => {
        // 이벤트 전파 방지 (파일 선택 다이얼로그가 열리는 것을 방지)
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!formData.title.trim()) {
            alert('보드명을 입력해주세요.');
            return;
        }

        // 썸네일 이미지 추가 체크박스가 체크되었는데 이미지가 없으면 알림
        if (formData.uploadThumbnail && !formData.thumbnailFile && !formData.thumbnailUrl) {
            alert('썸네일 이미지를 추가해주세요.');
            return;
        }

        // 템플릿 생성 체크 시 모든 필터 필수 (열개수 미선택 시 기본값 5)
        if (formData.autoTemplate) {
            const isMBrand = ['M', 'CB', '?', '-'].includes((activeBrdCd || 'M').toUpperCase());
            const missingFilters = [];
            if (!formData.mapType) missingFilters.push('템플릿');
            if (!formData.season) missingFilters.push('기획시즌');
            if (!isMBrand && !formData.adultKids) missingFilters.push('성인/키즈');
            if (!formData.category) missingFilters.push('대분류');
            if (!formData.itemDomain) missingFilters.push('아이템/도메인');
            if (missingFilters.length > 0) {
                alert(`템플릿 생성을 위해 모든 필터를 선택해주세요.\n미선택: ${missingFilters.join(', ')}`);
                return;
            }
            if (!formData.columnCount) {
                setFormData(prev => ({ ...prev, columnCount: '5' }));
            }
        }

        const title = formData.title.trim();

        try {
            if (editingItem) {
                // 편집 - 서버에 업데이트 요청 (file_id 기반)
                const response = await fetch('/api/matrix-board/update', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        fileId: editingItem.id,
                        title: title,
                        autoUpdate: formData.autoUpdate
                    })
                });

                const result = await response.json();
                if (!result.success) {
                    // 중복 에러 처리
                    alert(result.message || '보드 이름 변경 중 오류가 발생했습니다.');
                    // 편집 모드 유지 (이름 변경 실패 시)
                    return;
                }

                if (result.success) {
                    const autoLink = `/interstellar/matrix-board/${editingItem.id}`;

                    // 썸네일 체크박스가 해제되었고 원래 업로드된 썸네일이 있는 경우 삭제
                    // 삭제 후 자동 생성된 썸네일 URL을 받아옴
                    let deletedThumbnailUrl = null;
                    if (!formData.uploadThumbnail && editingItem.thumbnailUploaded && editingItem.thumbnailUrl) {
                        try {
                            const deleteResponse = await fetch('/api/matrix-board/delete-thumbnail', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    fileId: editingItem.id
                                })
                            });
                            const deleteResult = await deleteResponse.json();
                            if (deleteResult.success) {
                                console.log('썸네일 삭제 성공 (DB의 thumbnail_uploaded도 false로 업데이트됨)');
                                // 삭제 후 자동 생성된 썸네일 URL이 있으면 사용
                                deletedThumbnailUrl = deleteResult.thumbnailUrl || null;
                                if (deletedThumbnailUrl) {
                                    console.log('자동 생성된 썸네일 URL:', deletedThumbnailUrl);
                                }
                            } else {
                                console.error('썸네일 삭제 실패:', deleteResult.message);
                            }
                        } catch (error) {
                            console.error('썸네일 삭제 오류:', error);
                        }
                    }

                    // WebSocket으로 다른 클라이언트에게 알림
                    socket.emit('files-update', {
                        type: 'update',
                        file: {
                            id: editingItem.id,
                            title: title,
                            link: autoLink,
                            season: seasonValue,
                            itemDomain: formData.itemDomain,
                            columnCount: formData.columnCount,
                            autoTemplate: formData.autoTemplate,
                            autoUpdate: formData.autoUpdate
                        }
                    });

                    // 로컬 상태 업데이트 (추가 필드 포함)
                    setMenuItems(items =>
                        items.map(item =>
                            item.id === editingItem.id
                                ? {
                                    ...item,
                                    title: title,
                                    link: autoLink,
                                    season: formData.season || item.season,
                                    itemDomain: formData.itemDomain || item.itemDomain,
                                    columnCount: formData.columnCount || item.columnCount,
                                    autoTemplate: formData.autoTemplate !== undefined ? formData.autoTemplate : item.autoTemplate,
                                    autoUpdate: formData.autoUpdate !== undefined ? formData.autoUpdate : item.autoUpdate,
                                    // 썸네일 체크박스가 해제된 경우: 자동 생성된 썸네일 URL 사용 (있으면)
                                    thumbnailUrl: formData.uploadThumbnail
                                        ? item.thumbnailUrl
                                        : (deletedThumbnailUrl || item.thumbnailUrl || null),
                                    thumbnailUploaded: formData.uploadThumbnail ? item.thumbnailUploaded : false,
                                    // 썸네일이 변경되었으므로 업데이트 시간 갱신
                                    updatedAt: new Date().toISOString(),
                                    thumbnailUpdatedAt: Date.now()
                                }
                                : item
                        )
                    );

                    // 썸네일 업로드 처리
                    if (formData.uploadThumbnail && formData.thumbnailFile && editingItem.id) {
                        try {
                            const reader = new FileReader();
                            reader.onload = async (e) => {
                                const thumbnailData = e.target.result;
                                const thumbnailResponse = await fetch('/api/matrix-board/upload-thumbnail', {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({
                                        fileId: editingItem.id,
                                        thumbnailData: thumbnailData
                                    })
                                });
                                const thumbnailResult = await thumbnailResponse.json();
                                if (thumbnailResult.success) {
                                    const now = new Date().toISOString();
                                    const timestamp = Date.now();

                                    console.log('✅ [썸네일 업로드 성공]');

                                    // 해당 썸네일만 즉시 재로딩
                                    // 서버에서 updated_at이 업데이트되었으므로 현재 시간 사용
                                    setMenuItems(prev => prev.map(item => {
                                        if (item.id === editingItem.id) {
                                            return {
                                                ...item,
                                                thumbnailUrl: thumbnailResult.thumbnailUrl,
                                                thumbnailUploaded: true,
                                                updatedAt: now, // 서버에서 업데이트된 시간
                                                thumbnailUpdatedAt: timestamp // 이미지 재로딩 트리거 (밀리초)
                                            };
                                        }
                                        return item;
                                    }));

                                    // 로딩 상태 설정
                                    setImageLoadingStates(prevStates => ({
                                        ...prevStates,
                                        [editingItem.id]: true
                                    }));
                                } else {
                                    console.error('썸네일 업로드 실패:', thumbnailResult.message);
                                }
                            };
                            reader.readAsDataURL(formData.thumbnailFile);
                        } catch (error) {
                            console.error('썸네일 업로드 오류:', error);
                        }
                    }

                    handleCloseDialog();
                } else {
                    alert('카드 수정에 실패했습니다: ' + result.message);
                }
            } else {
                // 추가 - 서버에 추가 요청
                const createdBy = getUserName(); // 현재 로그인한 사용자 이름

                // 현재 선택된 탭에 따라 projectType 결정
                const projectType = activeNav === '공유 프로젝트' ? 'shared' :
                    activeNav === '개인 프로젝트' ? 'personal' : 'shared';

                const response = await fetch('/api/matrix-board/add', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        title: title,
                        createdBy: createdBy,
                        projectType: projectType,
                        brdCd: activeBrdCd,
                        mapType: formData.mapType,
                        season: seasonValue,
                        adultKids: formData.adultKids,
                        category: formData.category,
                        itemDomain: formData.itemDomain,
                        autoTemplate: formData.autoTemplate,
                        autoUpdate: formData.autoUpdate
                    })
                });

                const result = await response.json();
                if (!result.success) {
                    // 중복 에러 처리
                    alert(result.message || '보드 생성 중 오류가 발생했습니다.');
                    return;
                }

                if (result.success) {
                    // 서버에서 반환된 boardCd(있으면) 또는 file_id로 link 생성
                    const fileId = result.data?.id;
                    const autoLink = `/interstellar/matrix-board/${result.data?.boardCd || fileId}`;

                    // 서버에서 반환된 데이터에 추가 필드 포함
                    const fileDataWithFields = result.data ? {
                        ...result.data,
                        link: autoLink,
                        season: formData.season,
                        itemDomain: formData.itemDomain,
                        columnCount: formData.columnCount,
                        autoTemplate: formData.autoTemplate
                    } : null;

                    // WebSocket으로 다른 클라이언트에게 알림
                    if (fileDataWithFields) {
                        socket.emit('files-update', {
                            type: 'add',
                            file: fileDataWithFields
                        });
                    }

                    // 로컬 상태에 추가 필드 포함하여 업데이트
                    if (fileDataWithFields) {
                        setMenuItems(prev => {
                            const existing = prev.find(item => item.id === fileDataWithFields.id);
                            if (existing) {
                                return prev.map(item =>
                                    item.id === fileDataWithFields.id ? fileDataWithFields : item
                                );
                            }
                            return [...prev, fileDataWithFields];
                        });
                    }

                    // 썸네일 업로드 처리 함수 (템플릿 생성 전후에 모두 사용)
                    const uploadThumbnailIfNeeded = async () => {
                        if (formData.uploadThumbnail && formData.thumbnailFile && fileId) {
                            try {
                                const reader = new FileReader();
                                reader.onload = async (e) => {
                                    const thumbnailData = e.target.result;
                                    const thumbnailResponse = await fetch('/api/matrix-board/upload-thumbnail', {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                            fileId: fileId,
                                            thumbnailData: thumbnailData
                                        })
                                    });
                                    const thumbnailResult = await thumbnailResponse.json();
                                    if (thumbnailResult.success) {
                                        console.log('썸네일 업로드 성공:', thumbnailResult.thumbnailUrl);
                                    } else {
                                        console.error('썸네일 업로드 실패:', thumbnailResult.message);
                                    }
                                };
                                reader.readAsDataURL(formData.thumbnailFile);
                            } catch (error) {
                                console.error('썸네일 업로드 오류:', error);
                            }
                        }
                    };

                    // 템플릿 자동생성이 활성화된 경우 템플릿 생성
                    if (formData.autoTemplate && formData.season && formData.itemDomain && formData.columnCount) {
                        setTemplateLoading(true);
                        setTemplateProgress({ message: '템플릿 생성 준비 중...', progress: 0, currentSheet: 0, totalSheets: 0 });

                        // API 호출 중 진행률을 점진적으로 올리기 위한 인터벌 설정 (try 블록 밖에서 선언)
                        let progressInterval = null;
                        let currentProgress = 10;
                        const progressStep = 0.5; // 0.5%씩 증가
                        const progressIntervalMs = 200; // 200ms마다 업데이트

                        try {
                            // 템플릿 생성 API 호출 (서버에서 cadData를 직접 가져옴)
                            setTemplateProgress(prev => ({ ...prev, message: '데이터 조회 중...', progress: 10 }));

                            // 진행률을 점진적으로 올리는 인터벌 시작
                            progressInterval = setInterval(() => {
                                currentProgress = Math.min(currentProgress + progressStep, 90); // 최대 90%까지
                                setTemplateProgress(prev => ({
                                    ...prev,
                                    message: prev.message || '시트 생성',
                                    progress: Math.floor(currentProgress)
                                }));
                            }, progressIntervalMs);

                            const templateResponse = await fetch('/api/matrix-board/create-template', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    fileId: fileId,
                                    season: seasonValue,
                                    itemDomain: formData.itemDomain,
                                    columnCount: formData.columnCount,
                                    brdCd: activeBrdCd,
                                    adultKids: formData.adultKids,
                                    category: formData.category,
                                    mapType: formData.mapType
                                })
                            });

                            // API 응답이 오면 인터벌 정지
                            if (progressInterval) {
                                clearInterval(progressInterval);
                            }

                            const templateResult = await templateResponse.json();

                            if (templateResult.success) {
                                const totalSheets = templateResult.data?.totalSheets || templateResult.data?.sheets?.length || 1;

                                // 시트 생성 진행률 업데이트
                                if (totalSheets > 0) {
                                    const baseProgress = Math.max(currentProgress, 20); // 현재 진행률 또는 20% 중 큰 값
                                    const remainingProgress = 100 - baseProgress;
                                    const progressPerSheet = remainingProgress / totalSheets; // 남은 진행률을 시트 개수로 나눔

                                    // 각 시트 생성 시마다 진행률을 조금씩 올리기
                                    const updateProgress = (currentSheet) => {
                                        const sheetProgress = (currentSheet + 1) * progressPerSheet;
                                        const progress = Math.floor(Math.min(baseProgress + sheetProgress, 100));

                                        setTemplateProgress({
                                            message: `시트 생성 중... (${currentSheet + 1}/${totalSheets})`,
                                            progress: progress,
                                            currentSheet: currentSheet + 1,
                                            totalSheets: totalSheets
                                        });
                                    };

                                    // 시트 생성 진행률을 점진적으로 업데이트
                                    // 서버에서 시트를 생성하는 시간을 고려하여 각 시트마다 약간의 지연을 두고 진행률 업데이트
                                    for (let i = 0; i < totalSheets; i++) {
                                        setTimeout(() => {
                                            updateProgress(i);
                                        }, i * 500); // 각 시트마다 500ms 간격으로 진행률 업데이트
                                    }

                                    // 마지막 시트 생성 후 완료
                                    setTimeout(() => {
                                        setTemplateProgress({
                                            message: ``,
                                            progress: 100,
                                            currentSheet: totalSheets,
                                            totalSheets: totalSheets
                                        });

                                        setTimeout(async () => {
                                            setTemplateLoading(false);
                                            // 템플릿 생성 완료 후 썸네일 업로드
                                            await uploadThumbnailIfNeeded();
                                            alert(`보드가 생성되었습니다.\n${templateResult.message}`);
                                        }, 500);
                                    }, totalSheets * 500 + 500); // 마지막 시트 처리 시간 + 여유 시간
                                } else {
                                    setTemplateProgress({ message: '템플릿 생성 완료!', progress: 100, currentSheet: 0, totalSheets: 0 });
                                    setTimeout(async () => {
                                        setTemplateLoading(false);
                                        // 템플릿 생성 완료 후 썸네일 업로드
                                        await uploadThumbnailIfNeeded();
                                        alert(`보드가 생성되었습니다.\n${templateResult.message}`);
                                    }, 500);
                                }
                            } else {
                                if (progressInterval) {
                                    clearInterval(progressInterval);
                                }
                                setTemplateLoading(false);
                                alert(`보드는 생성되었지만 템플릿 생성에 실패했습니다: ${templateResult.message}`);
                            }
                        } catch (error) {
                            // 에러 발생 시 인터벌 정리
                            if (progressInterval) {
                                clearInterval(progressInterval);
                            }
                            console.error('템플릿 생성 오류:', error);
                            setTemplateLoading(false);
                            // 템플릿 생성 실패해도 썸네일 업로드 시도
                            await uploadThumbnailIfNeeded();
                            alert('보드는 생성되었지만 템플릿 생성 중 오류가 발생했습니다.');
                        }
                    } else {
                        // 템플릿 자동생성이 아닌 경우 썸네일 업로드 처리
                        await uploadThumbnailIfNeeded();
                        alert('보드가 추가되었습니다.');
                    }

                    // 서버에서 최신 목록 다시 불러오기
                    await loadCards();
                    handleCloseDialog();
                } else {
                    alert('카드 추가에 실패했습니다: ' + result.message);
                }
            }
        } catch (error) {
            console.error('카드 저장 오류:', error);
            alert('카드 저장 중 오류가 발생했습니다.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(`${selectedItemForMenu.title} 삭제`)) {
            try {
                const response = await fetch('/api/matrix-board/delete', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        fileId: id
                    })
                });

                const result = await response.json();
                if (result.success) {
                    // WebSocket으로 다른 클라이언트에게 알림
                    socket.emit('files-update', {
                        type: 'delete',
                        fileId: id
                    });
                    // 서버에서 최신 목록 다시 불러오기
                    await loadCards();
                } else {
                    alert('카드 삭제에 실패했습니다: ' + result.message);
                }
            } catch (error) {
                console.error('카드 삭제 오류:', error);
                alert('카드 삭제 중 오류가 발생했습니다.');
            }
        }
    };

    // 메뉴 열기
    const handleMenuOpen = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setSelectedItemForMenu(item);
    };

    // 메뉴 닫기
    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedItemForMenu(null);
    };

    // 메뉴에서 편집 선택
    const handleMenuEdit = () => {
        if (selectedItemForMenu) {
            handleOpenEdit(selectedItemForMenu);
        }
        handleMenuClose();
    };

    // 메뉴에서 삭제 선택
    const handleMenuDelete = () => {
        if (selectedItemForMenu) {
            handleDelete(selectedItemForMenu.id);
        }
        handleMenuClose();
    };

    // 메뉴에서 정보 선택
    const handleMenuInfo = () => {
        if (selectedItemForMenu) {
            setSelectedItemForInfo(selectedItemForMenu);
            setOpenInfoDialog(true);
        }
        handleMenuClose();
    };

    // 정보 모달 닫기
    const handleCloseInfoDialog = () => {
        setOpenInfoDialog(false);
        setSelectedItemForInfo(null);
    };

    // 메뉴에서 복제 선택 (공유/개인 프로젝트 타입 지정)
    const handleMenuDuplicate = async (projectType) => {
        if (!selectedItemForMenu) {
            handleMenuClose();
            return;
        }

        try {
            const originalItem = selectedItemForMenu;
            const createdBy = getUserName();

            // 복제 번호 계산 (보드1 (2), 보드1 (3) 등)
            // 서버의 generateDuplicateName과 동일한 로직 사용
            // 같은 project_type 내에서만 중복 체크
            const baseName = originalItem.title.replace(/\s*\(\d+\)$/, '');
            const currentProjectType = projectType || (activeNav === '공유 프로젝트' ? 'shared' :
                activeNav === '개인 프로젝트' ? 'personal' : 'shared');

            // 복제 대상 프로젝트 타입의 보드 목록을 서버에서 가져와서 중복 체크
            let targetProjectItems = [];
            try {
                let url = `/api/matrix-board/list?projectType=${currentProjectType}`;
                if (currentProjectType === 'personal') {
                    const userName = getUserName();
                    if (userName) {
                        url += `&createdBy=${encodeURIComponent(userName)}`;
                    }
                }
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                const result = await response.json();
                if (result.success) {
                    targetProjectItems = result.data || [];
                }
            } catch (error) {
                console.error('복제 대상 프로젝트 타입의 보드 목록 가져오기 실패:', error);
                // 실패 시 현재 menuItems 사용 (fallback)
                targetProjectItems = menuItems.filter(item => {
                    const itemProjectType = item.project_type || item.projectType ||
                        (activeNav === '공유 프로젝트' ? 'shared' : 'personal');
                    return itemProjectType === currentProjectType;
                });
            }

            // 같은 project_type을 가진 항목만 필터링
            const matchingNames = targetProjectItems
                .filter(item => {
                    // use_yn이 'N'이면 제외
                    if (item.use_yn === 'N' || item.useYn === 'N') return false;

                    const itemTitle = item.title;
                    if (itemTitle === baseName) return true;
                    const match = itemTitle.match(/^(.+?)\s*\((\d+)\)$/);
                    return match && match[1] === baseName;
                })
                .map(item => item.title);

            console.log('🔍 복제 번호 계산:', {
                baseName,
                currentProjectType,
                targetProjectItemsCount: targetProjectItems.length,
                matchingNames,
                originalTitle: originalItem.title
            });

            // 번호 추출 및 정렬 (기본 이름은 제외)
            const numbers = matchingNames
                .map(name => {
                    if (name === baseName) return null; // 기본 이름은 제외
                    const match = name.match(/\((\d+)\)$/);
                    return match ? parseInt(match[1]) : null;
                })
                .filter(num => num !== null)
                .sort((a, b) => a - b);

            const hasBaseName = matchingNames.some(name => name === baseName);

            // 동일 보드명이 없으면 원본 이름 그대로 사용
            let newTitle;
            if (!hasBaseName && numbers.length === 0) {
                // 동일 보드명이 없으면 +1 없이 원본 이름 그대로 사용
                newTitle = baseName;
            } else {
                // 다음 번호 계산
                // 기본 이름이 있으면 복제본은 (2)부터 시작 (기본 이름이 (1)의 역할)
                let nextNum = 2; // 기본 이름이 있으면 항상 (2)부터 시작

                // 기본 이름이 있으면 (2)부터 시작하므로, numbers에서 1을 제외하고 계산
                for (const num of numbers) {
                    // 1은 기본 이름을 의미하므로 건너뛰기
                    if (num === 1) continue;
                    // 현재 번호가 nextNum과 같으면 다음 번호로 증가
                    if (num === nextNum) {
                        nextNum++;
                    } else if (num > nextNum) {
                        // 현재 번호가 nextNum보다 크면 빈 번호를 찾은 것
                        break;
                    }
                }

                newTitle = `${baseName} (${nextNum})`;
            }

            // 서버에 보드 복제 요청 (시트와 노드 포함)
            const response = await fetch('/api/matrix-board/duplicate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    sourceFileId: originalItem.id,
                    newTitle: newTitle,
                    createdBy: createdBy,
                    projectType: projectType || (activeNav === '공유 프로젝트' ? 'shared' :
                        activeNav === '개인 프로젝트' ? 'personal' : 'shared')
                })
            });

            const result = await response.json();
            if (result.success) {
                // 서버에서 반환된 데이터에 추가 필드 포함
                const fileDataWithFields = result.data ? {
                    ...result.data,
                    season: originalItem.season,
                    itemDomain: originalItem.itemDomain,
                    columnCount: originalItem.columnCount,
                    autoTemplate: originalItem.autoTemplate
                } : null;

                // WebSocket으로 다른 클라이언트에게 알림
                if (fileDataWithFields) {
                    socket.emit('files-update', {
                        type: 'add',
                        file: fileDataWithFields
                    });
                }

                // 복제된 프로젝트 타입에 맞는 탭으로 전환
                if (projectType === 'shared') {
                    setActiveNav('공유 프로젝트');
                } else if (projectType === 'personal') {
                    setActiveNav('개인 프로젝트');
                }

                // 서버에서 최신 목록 다시 불러오기
                await loadCards();
            } else {
                alert('보드 복제에 실패했습니다: ' + result.message);
            }
        } catch (error) {
            console.error('보드 복제 오류:', error);
            alert('보드 복제 중 오류가 발생했습니다.');
        }

        handleMenuClose();
    };

    // 사용자 이름 가져오기 (MSAL에서)
    const getUserName = () => {
        if (accounts && accounts.length > 0) {
            const account = accounts[0];
            const fullName = account.name || account.username || 'User';

            // "amber(이경선) F/KR/DD/PRCS/AIE/BI" 형식에서 이름만 추출
            // 패턴: "이름(한글명)" 형식을 찾음
            const match = fullName.match(/^([^\s]+(?:\([^)]+\))?)/);
            if (match) {
                return match[1]; // "amber(이경선)" 반환
            }

            return fullName;
        }
        return 'User';
    };

    // 사용자 부서 정보 가져오기
    const getUserDepartment = () => {
        if (accounts && accounts.length > 0) {
            const account = accounts[0];

            // jobTitle이나 department 필드가 있으면 사용
            if (account.idTokenClaims?.jobTitle) {
                return account.idTokenClaims.jobTitle;
            }

            // name에서 부서 정보 추출 "amber(이경선) F/KR/DD/PRCS/AIE/BI" 형식
            const fullName = account.name || '';
            const match = fullName.match(/^[^\s]+(?:\([^)]+\))?\s+(.+)$/);
            if (match) {
                return match[1]; // "F/KR/DD/PRCS/AIE/BI" 반환
            }

            return '';
        }
        return '';
    };

    const getUserInitial = () => {
        const name = getUserName();
        return name ? name.charAt(0).toUpperCase() : 'U';
    };

    // 로그아웃 핸들러 (권한 체크 전에 선언)
    const handleLogout = () => {
        instance.logoutPopup({
            postLogoutRedirectUri: "/",
        }).catch(e => {
            console.error("로그아웃 실패:", e);
        });
    };

    const handleNavClick = (navItem) => {
        // 권한 관리 탭 클릭 시 권한 체크
        if (navItem === '권한 관리' && !hasPermission('accessManagement', 'view', activeBrdCd)) {
            alert('권한 관리 페이지에 접근할 수 있는 권한이 없습니다.');
            return;
        }
        setActiveNav(navItem);
        // localStorage에 activeNav 저장
        localStorage.setItem('matrixBoardActiveNav', navItem);
    };

    // 정렬된 아이템 계산
    const sortedItems = useMemo(() => {
        const items = [...menuItems];
        switch (sortOrder) {
            case 'latest-created':
                return items.sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return dateB - dateA; // 내림차순 (최신이 먼저)
                });
            case 'latest-updated':
                const sorted = items.sort((a, b) => {
                    const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    return dateB - dateA; // 내림차순 (최신이 먼저)
                });
                sorted.slice(0, 5).forEach((item, idx) => {
                });
                return sorted;
            case 'title-asc':
                return items.sort((a, b) => {
                    const titleA = (a.title || '').toLowerCase();
                    const titleB = (b.title || '').toLowerCase();
                    return titleA.localeCompare(titleB, 'ko'); // 오름차순
                });
            default:
                return items;
        }
    }, [menuItems, sortOrder]);

    // 권한 체크: mlbMatrixBoard + view 권한이 없으면 권한 없음 페이지 표시
    if (!userLoading && !hasPermission('matrixBoard-teamProject', 'view', activeBrdCd)) {
        return <Unauthorized title="Matrix Board" brd_cd={activeBrdCd || user?.brdCd} />;
    }

    return (
        <Container>
            <NavBar>
                <NavItems>
                    <NavItem
                        $active={activeNav === '공유 프로젝트'}
                        onClick={() => handleNavClick('공유 프로젝트')}
                    >
                        공유 프로젝트
                    </NavItem>
                    {!userLoading && hasPermission('matrixBoard-personalProject', 'view', activeBrdCd) && (
                        <NavItem
                            $active={activeNav === '개인 프로젝트'}
                            onClick={() => handleNavClick('개인 프로젝트')}
                        >
                            개인 프로젝트
                        </NavItem>
                    )}
                    <NavItem
                        $active={activeNav === 'CAD Dashboard'}
                        onClick={() => handleNavClick('CAD Dashboard')}
                    >
                        {activeBrdCd === 'M' ? 'CAD Dashboard' : activeBrdCd === 'X' ? 'Style Initial Plan' : 'Img Upload'}
                    </NavItem>
                    <NavItem
                        $active={activeNav === '사용 가이드'}
                        onClick={() => handleNavClick('사용 가이드')}
                    >
                        사용 가이드
                    </NavItem>
                    <NavItem
                        $active={activeNav === '문의/요청'}
                        onClick={() => handleNavClick('문의/요청')}
                    >
                        문의/요청
                    </NavItem>
                    {!userLoading && hasPermission('accessManagement', 'view', activeBrdCd) && (
                        <NavItem
                            $active={activeNav === '권한 관리'}
                            onClick={() => handleNavClick('권한 관리')}
                        >
                            권한 관리
                        </NavItem>
                    )}
                </NavItems>
                <UserProfile>
                    {/* 브랜드 선택 드롭다운 */}
                    {brands && brands.length > 0 && (
                        <>
                            <div
                                onClick={brands.length > 1 ? (e) => setBrandMenuAnchor(e.currentTarget) : undefined}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: 18, userSelect: 'none', marginRight: 8, ...(brands.length > 1 && { cursor: 'pointer' }) }}
                            >
                                {brands.find(b => b.brd_cd === activeBrdCd)?.brd_nm || activeBrdCd}
                                {brands.length > 1 && <span style={{ fontSize: 11, color: '#999' }}>▼</span>}
                            </div>
                            {brands.length > 1 && (
                                <Menu
                                    anchorEl={brandMenuAnchor}
                                    open={Boolean(brandMenuAnchor)}
                                    onClose={() => setBrandMenuAnchor(null)}
                                >
                                    {brands.map(b => (
                                        <MenuItem
                                            key={b.brd_cd}
                                            selected={b.brd_cd === activeBrdCd}
                                            onClick={() => {
                                                setActiveBrdCd(b.brd_cd);
                                                localStorage.setItem('matrixBoardActiveBrdCd', b.brd_cd);
                                                setBrandMenuAnchor(null);
                                                logAccess({ user: `${user?.userId}(${user?.name})`, email: user?.email, brd_cd: user?.brdCd || null, page_brd_cd: b.brd_cd, page_nm: 'matrix-board', detail: null });
                                            }}
                                        >
                                            {b.brd_nm}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            )}
                        </>
                    )}
                    <UserAvatar src={userPhoto}>
                        {!userPhoto && getUserInitial()}
                    </UserAvatar>
                    <UserInfo>
                        <UserName>{getUserName()}</UserName>
                        {getUserDepartment() && (
                            <UserDepartment>{getUserDepartment()}</UserDepartment>
                        )}
                    </UserInfo>
                    <LogoutButton
                        onClick={handleLogout}
                    >
                        <Logout style={{ fontSize: '20px' }} />
                    </LogoutButton>
                </UserProfile>
            </NavBar>
            <ContentArea>
                {activeNav === 'CAD Dashboard' ? (
                    <CadDataContainer>
                        {activeBrdCd === 'X'
                            ? (xSubTab === 'upload'
                                ? <CadDataDXUpload onSubTabChange={setXSubTab} />
                                : <CadDataDX onSubTabChange={setXSubTab} />)
                            : activeBrdCd === 'M'
                            ? <CadData />
                            : <StyleColorImg brdCd={activeBrdCd} />}
                    </CadDataContainer>
                ) : activeNav === '사용 가이드' ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '32px 24px', height: '100%', boxSizing: 'border-box', background: '#f4f4f2', overflowY: 'auto' }}>
                        <iframe
                            src="https://static-dashff.fnf.co.kr/interstellar/matrix_board/%EB%A7%A4%ED%8A%B8%EB%A6%AD%EC%8A%A4%EB%B3%B4%EB%93%9C%20%EA%B0%80%EC%9D%B4%EB%93%9C.pdf#toolbar=0&navpanes=0&scrollbar=0"
                            style={{ width: '1200px', height: '-webkit-fill-available', border: '1px solid #ddd', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', background: '#fff', flexShrink: 0 }}
                            title="사용 가이드"
                        />
                    </div>
                ) : activeNav === '문의/요청' ? (
                    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
                        <iframe
                            src="https://static-dashff.fnf.co.kr/interstellar/matrix_board/request.html"
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            title="요청사항"
                            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, border: 0 }}
                        />
                    </div>
                ) : activeNav === '권한 관리' ? (
                    !userLoading && hasPermission('accessManagement', 'view', activeBrdCd) ? (
                        <AccessManagement onBack={() => handleNavClick('공유 프로젝트')} />
                    ) : (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            color: '#666'
                        }}>
                            <h2>권한이 없습니다</h2>
                            <p>권한 관리 페이지에 접근할 수 있는 권한이 없습니다.</p>
                        </div>
                    )
                ) : (
                    <div style={{ padding: '24px' }}>
                        <SortContainer>
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                <Select
                                    value={sortOrder}
                                    onChange={(e) => { setSortOrder(e.target.value); loadCards(); }}
                                    sx={{
                                        height: '36px',
                                        fontSize: '13px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                        },
                                    }}
                                >
                                    <MenuItem value="latest-created">최신 생성</MenuItem>
                                    <MenuItem value="latest-updated">최신 수정</MenuItem>
                                    <MenuItem value="title-asc">제목</MenuItem>
                                </Select>
                            </FormControl>
                        </SortContainer>
                        <CardsContainer>
                            {sortedItems.map((item, index) => (
                                <StyledCard
                                    key={item.id}
                                    elevation={0}
                                >
                                    <CardContentStyled>
                                        <MenuLink to={item.link} state={{ fromTab: activeNav }} onClick={() => logAccess({ user: `${user?.userId}(${user?.name})`, email: user?.email, brd_cd: user?.brdCd || null, page_brd_cd: activeBrdCd, page_nm: 'matrix-board', detail: item.title })}>
                                            <ThumbnailPreview>
                                                {/* 썸네일이 있을 때만 표시 */}
                                                {item.thumbnailUrl ? (
                                                    <>
                                                        {/* 스켈레톤 로딩 */}
                                                        {(imageLoadingStates[item.id] === true || imageLoadingStates[item.id] === undefined) && (
                                                            <LoadingContainer />
                                                        )}

                                                        {/* 썸네일 이미지 */}
                                                        <ThumbnailImageWithTimeout
                                                            item={item}
                                                            imageLoadingState={imageLoadingStates[item.id]}
                                                            onLoadSuccess={() => {
                                                                setImageLoadingStates(prev => ({
                                                                    ...prev,
                                                                    [item.id]: false
                                                                }));
                                                            }}
                                                            onLoadError={() => {
                                                                setImageLoadingStates(prev => ({
                                                                    ...prev,
                                                                    [item.id]: 'error'
                                                                }));
                                                            }}
                                                            onLoadStart={() => {
                                                                if (imageLoadingStates[item.id] === undefined) {
                                                                    setImageLoadingStates(prev => ({
                                                                        ...prev,
                                                                        [item.id]: true
                                                                    }));
                                                                }
                                                            }}
                                                        />
                                                    </>
                                                ) : null}
                                                {/* 썸네일이 없으면 빈칸만 표시 (그리드 제거) */}
                                            </ThumbnailPreview>
                                        </MenuLink>
                                    </CardContentStyled>
                                    <CardFooter title={item.title}>
                                        <FooterContent>
                                            <MenuTitle>{item.title}</MenuTitle>
                                            <CardMeta>
                                                {item.updatedAt ? formatTimeAgo(item.updatedAt) : '방금 전'} {item.updatedBy ? item.updatedBy : (item.createdBy ? item.createdBy : '편집')}
                                            </CardMeta>
                                        </FooterContent>
                                        {!userLoading && hasPermission(activeNav === '개인 프로젝트' ? 'matrixBoard-personalProject' : 'matrixBoard-teamProject', 'edit', activeBrdCd) && (
                                            <CardMenuButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, item)}
                                            >
                                                <MoreVert fontSize="small" />
                                            </CardMenuButton>
                                        )}
                                    </CardFooter>
                                </StyledCard>
                            ))}
                            {!userLoading && hasPermission(activeNav === '개인 프로젝트' ? 'matrixBoard-personalProject' : 'matrixBoard-teamProject', 'edit', activeBrdCd) && (
                                <AddCard onClick={handleOpenAdd}>
                                    <AddIcon />
                                </AddCard>
                            )}
                        </CardsContainer>
                    </div>
                )}
            </ContentArea>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleMenuEdit}>편집</MenuItem>
                {hasPermission('matrixBoard-teamProject', 'edit') && <MenuItem onClick={() => handleMenuDuplicate('shared')}>복제(공유)</MenuItem>}
                <MenuItem onClick={() => handleMenuDuplicate('personal')}>복제(개인)</MenuItem>
                <MenuItem onClick={handleMenuInfo}>정보</MenuItem>
                <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>삭제</MenuItem>
            </Menu>



            <StyledDialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitleStyled>
                    {editingItem ? '보드 편집' : '신규 보드 생성'}
                </DialogTitleStyled>
                <DialogContent>
                    {/* 보드명 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
                        <Typography sx={{ width: 130, fontSize: 14, color: '#444', flexShrink: 0, textAlign: 'left' }}>
                            보드명 <span style={{ color: '#e53935' }}>*</span>
                        </Typography>
                        <TextField
                            autoFocus
                            size="small"
                            fullWidth
                            variant="outlined"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />
                    </Box>

                    {/* 템플릿 생성 */}
                    {!editingItem && <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>

                        <Typography sx={{ width: 130, fontSize: 14, color: '#444', flexShrink: 0, textAlign: 'left' }}>
                            템플릿 생성
                        </Typography>
                        <Checkbox
                            size="small"
                            checked={formData.autoTemplate}
                            onChange={(e) => setFormData(prev => ({ ...prev, autoTemplate: e.target.checked }))}
                            sx={{ p: 0.5, color: '#ccc5b9', '&.Mui-checked': { color: '#a89f91' } }}
                        />
                    </Box>}

                    {/* 템플릿 옵션 (체크 시) */}
                    {!editingItem && formData.autoTemplate && (
                        <Box sx={{ mt: 1.5, ml: '146px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <FormControl size="small">
                                <InputLabel>템플릿</InputLabel>
                                <Select value={formData.mapType} label="템플릿" onChange={(e) => setFormData({ ...formData, mapType: e.target.value })}>
                                    {mapTypeOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>기획시즌</InputLabel>
                                <Select value={formData.season} label="기획시즌" onChange={(e) => setFormData({ ...formData, season: e.target.value })}>
                                    {seasonOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </FormControl>
                            {['M', 'CB', '?', '-'].includes((activeBrdCd || 'M').toUpperCase()) ? (
                                <FormControl size="small" disabled>
                                    <InputLabel shrink>성인/키즈</InputLabel>
                                    <Select value="성인" label="성인/키즈" notched>
                                        <MenuItem value="성인">성인</MenuItem>
                                    </Select>
                                </FormControl>
                            ) : (
                                <FormControl size="small">
                                    <InputLabel>성인/키즈</InputLabel>
                                    <Select value={formData.adultKids} label="성인/키즈" onChange={(e) => setFormData({ ...formData, adultKids: e.target.value })}>
                                        {adultKidsOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                            <FormControl size="small">
                                <InputLabel>대분류</InputLabel>
                                <Select value={formData.category} label="대분류" onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                                    {categoryOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>시트구분</InputLabel>
                                <Select value={formData.itemDomain} label="시트구분" onChange={(e) => setFormData({ ...formData, itemDomain: e.target.value })}>
                                    {itemDomainOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size="small">
                                <InputLabel>열개수</InputLabel>
                                <Select value={formData.columnCount} label="열개수" onChange={(e) => setFormData({ ...formData, columnCount: e.target.value })}>
                                    {columnCountOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* 템플릿 자동 업데이트 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
                        <Typography sx={{ width: 130, fontSize: 14, color: '#444', flexShrink: 0, textAlign: 'left' }}>
                            템플릿 자동 업데이트
                        </Typography>
                        <Checkbox
                            size="small"
                            checked={formData.autoUpdate}
                            onChange={(e) => setFormData({ ...formData, autoUpdate: e.target.checked })}
                            sx={{ p: 0.5, color: '#ccc5b9', '&.Mui-checked': { color: '#a89f91' } }}
                        />
                    </Box>

                    {/* 썸네일 생성 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
                        <Typography sx={{ width: 130, fontSize: 14, color: '#444', flexShrink: 0, textAlign: 'left' }}>
                            썸네일 생성
                        </Typography>
                        <Checkbox
                            size="small"
                            checked={formData.uploadThumbnail}
                            onChange={(e) => setFormData({ ...formData, uploadThumbnail: e.target.checked })}
                            sx={{ p: 0.5, color: '#ccc5b9', '&.Mui-checked': { color: '#a89f91' } }}
                        />
                    </Box>
                    {formData.uploadThumbnail && (
                        <>
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none', marginLeft: '90px' }}
                                id="thumbnail-file-input"
                                onChange={handleThumbnailFileSelect}
                            />
                            {!formData.thumbnailFile && !formData.thumbnailUrl && (
                                <Box sx={{ ml: '80px', mt: 1 }}>
                                    <ThumbnailUploadArea
                                        isDragging={isDraggingThumbnail}
                                        onDragOver={handleThumbnailDragOver}
                                        onDragLeave={handleThumbnailDragLeave}
                                        onDrop={handleThumbnailDrop}
                                        style={{ cursor: 'default' }}
                                    >
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                            이미지를 드래그 / 붙여넣기 (Ctrl+V)
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                mt: 1,
                                                borderColor: '#ccc5b9',
                                                color: '#2c3e50',
                                                '&:hover': {
                                                    borderColor: '#b8b0a5',
                                                    background: 'rgba(204, 197, 185, 0.1)',
                                                }
                                            }}
                                        >
                                            파일 선택
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                onChange={handleThumbnailFileSelect}
                                            />
                                        </Button>
                                    </ThumbnailUploadArea>
                                </Box>
                            )}
                            {(formData.thumbnailFile || formData.thumbnailUrl) && (
                                <ThumbnailPreviewArea>
                                    <ThumbnailPreviewImage
                                        src={formData.thumbnailFile
                                            ? URL.createObjectURL(formData.thumbnailFile)
                                            : (() => {
                                                // 기존 썸네일 URL에 캐시 무효화를 위한 타임스탬프 추가 (맥 크롬 캐시 우회)
                                                const url = formData.thumbnailUrl;
                                                if (!url) return '';
                                                const timestamp = editingItem?.updatedAt
                                                    ? new Date(editingItem.updatedAt).getTime()
                                                    : Date.now();
                                                const cacheBuster = `${timestamp}_${editingItem?.id || ''}`;
                                                return `${url}${url.includes('?') ? '&' : '?'}t=${timestamp}&v=${cacheBuster}`;
                                            })()}
                                        alt="썸네일 미리보기"
                                        key={formData.thumbnailFile ? 'file' : `url-${editingItem?.updatedAt || Date.now()}-${editingItem?.id || ''}`}
                                    />
                                    <RemoveThumbnailButton
                                        size="small"
                                        onClick={handleRemoveThumbnail}
                                    >
                                        <Delete fontSize="small" />
                                    </RemoveThumbnailButton>
                                </ThumbnailPreviewArea>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{
                            color: '#666',
                            '&:hover': {
                                background: 'rgba(204, 197, 185, 0.1)',
                            }
                        }}
                    >
                        취소
                    </Button>
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSave(e);
                        }}
                        variant="contained"
                        sx={{
                            background: '#ccc5b9',
                            color: '#2c3e50',
                            '&:hover': {
                                background: '#b8b0a5',
                            }
                        }}
                    >
                        저장
                    </Button>
                </DialogActions>
            </StyledDialog>

            {/* 정보 모달 */}
            <StyledDialog
                open={openInfoDialog}
                onClose={handleCloseInfoDialog}
                maxWidth="xs"
                fullWidth
                TransitionProps={{ timeout: 0 }}
                sx={{
                    '& .MuiDialog-container': {
                        transition: 'none !important',
                    },
                    '& .MuiDialog-paper': {
                        transition: 'none !important',
                    },
                    '& .MuiBackdrop-root': {
                        transition: 'none !important',
                    }
                }}
            >
                <DialogTitleStyled>
                    보드 정보
                </DialogTitleStyled>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedItemForInfo && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 600, minWidth: '120px' }}>
                                    제목
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#2c3e50' }}>
                                    {selectedItemForInfo.title || '-'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 600, minWidth: '120px' }}>
                                    생성일자
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#2c3e50' }}>
                                    {formatDateTime(selectedItemForInfo.createdAt)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 600, minWidth: '120px' }}>
                                    생성자
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#2c3e50' }}>
                                    {selectedItemForInfo.createdBy || '-'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 600, minWidth: '120px' }}>
                                    시트 수
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#2c3e50' }}>
                                    {selectedItemForInfo.sheetCount !== undefined ? selectedItemForInfo.sheetCount : '-'}개
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 600, minWidth: '120px' }}>
                                    마지막 편집시간
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#2c3e50' }}>
                                    {formatDateTime(selectedItemForInfo.updatedAt)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseInfoDialog}
                        variant="contained"
                        sx={{
                            background: '#ccc5b9',
                            color: '#2c3e50',
                            '&:hover': {
                                background: '#b8b0a5',
                            }
                        }}
                    >
                        닫기
                    </Button>
                </DialogActions>
            </StyledDialog>

            {/* 템플릿 생성 로딩 다이얼로그 */}
            <Dialog
                open={templateLoading}
                onClose={() => { }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: '24px'
                    }
                }}
            >
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                        템플릿 생성 중...
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
                        {templateProgress.message}
                    </Typography>
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={templateProgress.progress}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'rgba(204, 197, 185, 0.2)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#ccc5b9',
                                    borderRadius: 4
                                }
                            }}
                        />
                        <Typography variant="caption" sx={{ mt: 1, color: '#999', display: 'block' }}>
                            {Math.floor(templateProgress.progress)}%
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </Container>
    );
}

export default Files;